// The account notices above every user-app screen (phase 4):
//   activation   a Google sign-up still "menunggu_aktivasi": only plain chat
//                works until the admin activates it after payment.
//   reconnect    an active Google account whose Drive access was revoked or
//                never granted (GET /auth/google/connection): attachments
//                need it, so the notice points at Profil.
// Also the full screen a deactivated ("nonaktif") account sees instead of the app.
import { useEffect, useState } from "react";
import * as API from "../api";
import type { User } from "../api";
import { useAuth } from "../auth";
import { isActiveAccount } from "../lib/account";
import { PRODUCT_NAME } from "../lib/product";
import { Notice, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";

export const ACTIVATION_TEXT = "Akun Anda menunggu aktivasi. Hubungi admin setelah pembayaran.";
export const RECONNECT_TEXT = "Hubungkan ulang Google Drive di Profil untuk mengirim lampiran";
export const DISABLED_TEXT = "Akun Anda dinonaktifkan. Hubungi admin.";

/** GET /auth/google/connection for an active Google account; null otherwise or while loading. */
export function useDriveConnection(user: User | null): API.DriveConnection | null {
  const [conn, setConn] = useState<API.DriveConnection | null>(null);
  const key = user && user.authProvider === "google" && isActiveAccount(user) ? user.id : null;
  useEffect(() => {
    setConn(null);
    if (!key) return;
    let alive = true;
    API.getDriveConnection().then(
      (c) => alive && setConn(c),
      () => alive && setConn(null),
    );
    return () => {
      alive = false;
    };
  }, [key]);
  return conn;
}

export type BannerKind = "activation" | "reconnect" | null;

export function bannerFor(user: User | null, conn: API.DriveConnection | null): BannerKind {
  if (!user || user.role === "pending") return null;
  if (!isActiveAccount(user)) return "activation";
  if (conn && (conn.status === "revoked" || conn.status === "missing_scope")) return "reconnect";
  return null;
}

export function AccountBanner({ kind }: { kind: BannerKind }) {
  if (kind === "activation") {
    return (
      <div className="account-banner no-print">
        <Notice tone="info" title={ACTIVATION_TEXT}>
          <p>Sementara menunggu, Anda tetap dapat bertanya kepada Pluto lewat chat.</p>
        </Notice>
      </div>
    );
  }
  if (kind === "reconnect") {
    return (
      <div className="account-banner no-print">
        <Notice
          tone="warning"
          title={RECONNECT_TEXT}
          action={
            <a className="btn btn-secondary" href="/profil">
              Buka Profil
            </a>
          }
        />
      </div>
    );
  }
  return null;
}

/** What a deactivated account sees in place of the app. */
export function DisabledScreen() {
  useTitle("Akun dinonaktifkan");
  const { signOut } = useAuth();
  const [leaving, setLeaving] = useState(false);
  return (
    <main id="main" className="login" data-ui-shell="none" data-jal-shell="none">
      <div className="login-column">
        <section className="card login-card" aria-labelledby="disabled-title">
          <header className="login-head">
            <div className="login-head-text">
              <h1 id="disabled-title" className="login-title">
                {PRODUCT_NAME}
              </h1>
            </div>
          </header>
          <div className="login-body">
            <Notice tone="warning" title={DISABLED_TEXT} />
            <button
              type="button"
              className="btn btn-secondary btn-block"
              aria-busy={leaving || undefined}
              onClick={async () => {
                setLeaving(true);
                await signOut();
              }}
            >
              <Glyph name="logout" />
              <span>Keluar</span>
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}
